/**
 * The country-dial-code list, in the shape every country / Code picker reads.
 *
 * ── Two sources, split by whether anyone is signed in ─────────────────
 * Signed-in CRM screens (Create Partner, GST, Manual Flight, Manual Hotel)
 * read the backend's own country table — `GET /app/v1/county`, fetched ONCE
 * per login and held in admin/contexts/CountriesContext.jsx, reached through
 * `usePhoneCodes()`.
 *
 * The bundled `src/constants/Phone_Codes.json`, read through `loadPhoneCodes()`
 * below, serves three cases:
 *   • the partner signup form — the one picker that renders BEFORE login, when
 *     there is no `x-access-token` for that route (via
 *     partner/hooks/useSignupPhoneCodes.js);
 *   • the partner portal's GST page, which needs a branch's country as an ISO
 *     code and has no CountriesContext (same hook);
 *   • the CRM screens' FALLBACK, only when that once-per-login API call fails.
 * The JSON is a snapshot of the same endpoint, so both sources go through the
 * one `buildPhoneCodes`.
 *
 * ── Why the JSON is a dynamic import ──────────────────────────────────
 * Imported at the top of a module, the JSON is BUNDLED into that module's
 * initial chunk and delays its first paint. `import()` makes webpack emit
 * it as a SEPARATE chunk fetched only when `loadPhoneCodes()` is called.
 *
 * The result is cached at the module level, with one shared in-flight
 * promise so concurrent callers coalesce into a single import (the pattern
 * in `src/admin/hooks/useTenantCurrency.js`).
 */

let cache = null;
let pending = null;

/** What a picker gets before the list arrives, or when it could not load. */
export const EMPTY_PHONE_CODES = { codes: [], byIso: {}, dialCodeFor: () => '' };

/**
 * Turn country rows (`{ name, code, iso, is_active? }`) into the shape the
 * picker consumes.
 *
 * Keyed by ISO (not dial code) so United States and Canada — and every
 * other country sharing `+1` (or `+7`, or any repeat) — appear as
 * SEPARATE rows in the picker. The old dedup-by-dial-code build lost
 * every second country silently.
 *
 * Junk rows are filtered here (blank code, non-alpha ISO) so callers
 * see clean data, and so is a country deactivated in the backend table
 * (`is_active: 0`). The bundled JSON carries no `is_active`, so none of
 * its rows are dropped by that check.
 */
export const buildPhoneCodes = (rows) => {
  const byIso = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (r?.is_active === 0 || r?.is_active === false) return;
    const digits = String(r?.code || '').replace(/[^\d]/g, '');
    const iso = String(r?.iso || '').trim().toUpperCase();
    if (!digits || !/^[A-Z]{2,3}$/.test(iso)) return;
    if (byIso.has(iso)) return;
    byIso.set(iso, {
      value: iso,
      dialCode: `+${digits}`,
      name: r.name || iso,
      label: `${r.name || iso} (+${digits})`,
    });
  });
  const codes = Array.from(byIso.values()).sort((a, b) => a.name.localeCompare(b.name));
  const byIsoMap = Object.fromEntries(codes.map((c) => [c.value, c.dialCode]));
  return {
    codes,
    byIso: byIsoMap,
    dialCodeFor: (iso) => byIsoMap[iso] || '',
  };
};

/**
 * Kick off (or reuse) the async load of the BUNDLED list — the pre-login
 * source only; see the header.
 *
 * Returns `cache` immediately on the second and subsequent calls. While the
 * first call is in-flight, every other caller waits on the same `pending`
 * promise — one import per session, not one per component.
 */
export const loadPhoneCodes = () => {
  if (cache) return Promise.resolve(cache);
  if (pending) return pending;
  pending = import(/* webpackChunkName: "phone-codes" */ 'constants/Phone_Codes.json')
    .then((mod) => {
      cache = buildPhoneCodes(mod?.default?.data);
      pending = null;
      return cache;
    })
    .catch((err) => {
      pending = null;
      // Cache an empty result so we don't retry on every render — the
      // picker will just show no options, which is the same failure
      // mode a caller who ignores `loading` would see.
      cache = EMPTY_PHONE_CODES;
      // eslint-disable-next-line no-console
      console.warn('[phoneCodes] failed to load Phone_Codes.json', err);
      return cache;
    });
  return pending;
};

/**
 * Synchronous peek at the bundled-list cache — for the signup hook's
 * `useState` initialiser, so a re-mount after the first load doesn't flash
 * a spinner. Returns `null` when nothing has loaded yet.
 */
export const getCachedPhoneCodes = () => cache;

/**
 * The ISO code for a country NAME as branch records carry it (`"India"`),
 * read off a picker list (`codes` from `usePhoneCodes()` / the bundled list).
 *
 * Matches the name ignoring case and surrounding spaces; a value that already
 * is one of the list's ISO codes (`"IN"`) is accepted as is. Answers `''` when
 * nothing matches — never a guess — so a caller can refuse to send rather
 * than send the wrong country. Names the list spells differently, or lacks
 * (the bundled JSON has no GB or KR), therefore answer `''`.
 */
export const isoForCountryName = (name, codes) => {
  const wanted = String(name ?? '').trim();
  if (!wanted || !Array.isArray(codes)) return '';
  const lower = wanted.toLowerCase();
  const byName = codes.find((c) => String(c?.name ?? '').trim().toLowerCase() === lower);
  if (byName) return byName.value;
  const upper = wanted.toUpperCase();
  return codes.some((c) => c?.value === upper) ? upper : '';
};
